if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      const addons = this.selectedAddons();
      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        }
      };

      if (addons.length) {
        // Parent line plus one line per ticked add-on, in a single atomic request.
        fetchRequestOpts.headers['Content-Type'] = 'application/json';
        fetchRequestOpts.body = JSON.stringify({
          items: this.buildAddonItems(addons),
          sections_url: window.location.pathname,
          sections
        });
      } else {
        const formData = new FormData(this.form);
        formData.append('sections_url', window.location.pathname);
        formData.append('sections', sections);
        fetchRequestOpts.body = formData;
      }

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        // A multi-item add responds with { items: [...] }; the parent line is first.
        const added = data.items ? data.items[0] : data;
        addons.forEach((addon) => addon.reset());

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === added.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: added.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === added.quantity)
                ? itemInOldCart.quantity : added.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: added.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Ticked <product-addon> elements inside this form (empty when the section has no
     * add-on blocks or nothing is ticked).
     * @returns {Array}
     */
    selectedAddons() {
      return Array.from(this.form.querySelectorAll('product-addon')).filter((addon) => addon.selected);
    }

    /**
     * Builds the `items` array for a multi-item /cart/add.js request: the parent line
     * from the form, then one line per ticked add-on. Every line shares a fresh
     * `_addon_group` id; add-on lines also carry `_addon_mode`, `_addon_parent` and a
     * copy of the shopper's text so it shows on both lines. cart-addons.js keeps the
     * group consistent afterwards. See docs/product-addons-plan.md.
     * @param {Array} addons - Ticked product-addon elements.
     * @returns {Array}
     */
    buildAddonItems(addons) {
      const groupId = Math.random().toString(16).slice(2, 10);
      const formData = new FormData(this.form);
      const strings = window.theme && theme.strings ? theme.strings : {};
      const forLabel = strings.addonForLabel || 'For';

      // The add-on text inputs sit inside the product form, so FormData carries them;
      // they belong on the add-on line only, not the bottle.
      const addonFieldNames = new Set();
      addons.forEach((addon) => addon.propertyNames.forEach((name) => addonFieldNames.add(name)));

      const parentId = Number(formData.get('id'));
      const parentQty = Math.max(1, Number(formData.get('quantity')) || 1);
      // Same-named inputs (a checkbox group) arrive as several entries; join them.
      const propValues = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (!match || typeof value !== 'string' || addonFieldNames.has(match[1])) return;
        const trimmed = value.trim();
        if (trimmed) (propValues[match[1]] = propValues[match[1]] || []).push(trimmed);
      });
      const parentProps = { _addon_group: groupId };
      Object.keys(propValues).forEach((name) => {
        parentProps[name] = propValues[name].join(', ');
      });
      // The bottle stores only the hidden group id. Its visible "Add-ons" line in the
      // cart is computed by cart-items.liquid from the add-on lines actually present,
      // so it can never go stale; the durable link on the order is the add-on line's
      // "For" property.

      const parent = { id: parentId, quantity: parentQty, properties: parentProps };
      if (formData.get('selling_plan')) parent.selling_plan = Number(formData.get('selling_plan'));

      const items = [parent];
      addons.forEach((addon) => {
        items.push({
          id: Number(addon.variantId),
          quantity: addon.mode === 'match' ? parentQty : 1,
          properties: {
            // "For: Grey Goose Vodka 750ML" first, then the shopper's text.
            [forLabel]: addon.parentTitle,
            ...addon.properties,
            _addon_group: groupId,
            _addon_mode: addon.mode,
            _addon_parent: String(parentId)
          }
        });
      });

      return items;
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
